import Layout from "../../Layout/Layout.component";
import { AppContext } from "../../Context/MainContext";
import { useContext, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { formatCurrency } from "../../utils/number.utils";
import InfoCard from "../../Components/InfoCard.component";

const GeneralControlSheetDashboardPage = () => {

    const { transactions, controlSheets } = useContext(AppContext);

    const [data, setData] = useState<any[]>([]);
    const [resume, setResume] = useState<any>({});
    const [showAllApproved, setShowAllApproved] = useState(false);
    const [selectedFamily, setSelectedFamily] = useState<string>("Todas las familias");
    const [availableFamilies, setAvailableFamilies] = useState<string[]>([]);

    useEffect(() => {
        if (controlSheets.length > 0) {
            const familiesSet = new Set<string>();

            // Map ControlSheet-Families to aggregations
            let csFamilyMap: { [key: string]: any } = {};

            controlSheets.forEach((cs: any) => {
                if (cs.codes && cs.codes.length > 0) {
                    cs.codes.forEach((code: any) => {
                        const family = code.family;
                        if (family) familiesSet.add(family);
                        // Unique key for ControlSheet + Family
                        const key = `${cs._id}-${family}`;

                        if (!csFamilyMap[key]) {
                            const familyAgreements = (cs.agreements || []).filter((a: any) => a.family === family);
                            const agreementsTotal = familyAgreements.reduce((acc: number, curr: any) => acc + (curr.amount || 0), 0);

                            csFamilyMap[key] = {
                                key: key,
                                controlSheetName: cs.name,
                                family: family,
                                controlSheetId: cs._id,
                                rowName: `${cs.name} - ${family}`,
                                agreementsTotal: agreementsTotal,
                                invoicesTotal: 0,
                                nnccTotal: 0
                            };
                        }
                    });
                } else {
                    // If a control sheet has no families, we still might want to show it or at least capture its transactions if any
                    familiesSet.add("Sin familia");
                    const key = `${cs._id}-Sin familia`;
                    if (!csFamilyMap[key]) {
                        const familyAgreements = (cs.agreements || []).filter((a: any) => a.family === "Sin familia" || !a.family);
                        const agreementsTotal = familyAgreements.reduce((acc: number, curr: any) => acc + (curr.amount || 0), 0);

                        csFamilyMap[key] = {
                            key: key,
                            controlSheetName: cs.name,
                            family: "Sin familia",
                            controlSheetId: cs._id,
                            rowName: `${cs.name}`,
                            agreementsTotal: agreementsTotal,
                            invoicesTotal: 0,
                            nnccTotal: 0
                        };
                    }
                }
            });

            // Calculate invoices and NNCCs from transactions grouped by ControlSheet and family
            // Filter Facturas by controlSheet existence and paymentStatus check
            let facturas = transactions.filter((transaction: any) =>
                transaction.type === 'FACTURA' &&
                transaction.status &&
                (transaction.status === 'Aprobada' || transaction.status === 'Ingresada') &&
                transaction.controlSheet
            );

            if (!showAllApproved) {
                facturas = facturas.filter((transaction: any) => transaction.paymentStatus && transaction.paymentStatus.toLowerCase().includes('pagada'));
            }

            facturas.forEach((transaction: any) => {
                const family = transaction.family || "Sin familia";
                familiesSet.add(family);
                const csId = transaction.controlSheet._id;
                const key = `${csId}-${family}`;

                if (csFamilyMap[key]) {
                    csFamilyMap[key].invoicesTotal += (transaction.total || 0);
                } else {
                    // Create if exists a transaction for a control sheet but the family wasn't in codes
                    const csName = transaction.controlSheet.name || csId;
                    const cs = controlSheets.find((c: any) => c._id === csId);
                    const familyAgreements = (cs?.agreements || []).filter((a: any) => a.family === family);
                    const agreementsTotal = familyAgreements.reduce((acc: number, curr: any) => acc + (curr.amount || 0), 0);

                    csFamilyMap[key] = {
                        key: key,
                        controlSheetName: csName,
                        family: family,
                        controlSheetId: csId,
                        rowName: family === "Sin familia" ? csName : `${csName} - ${family}`,
                        agreementsTotal: agreementsTotal,
                        invoicesTotal: transaction.total || 0,
                        nnccTotal: 0
                    };
                }
            });

            // Calculate NNCCs
            let nnccs = transactions.filter((transaction: any) =>
                transaction.type === 'NNCC' &&
                transaction.controlSheet
            );

            nnccs.forEach((transaction: any) => {
                const family = transaction.family || "Sin familia";
                familiesSet.add(family);
                const csId = transaction.controlSheet._id;
                const key = `${csId}-${family}`;

                if (csFamilyMap[key]) {
                    csFamilyMap[key].nnccTotal += (transaction.total || 0);
                } else {
                    const csName = transaction.controlSheet.name || csId;
                    const cs = controlSheets.find((c: any) => c._id === csId);
                    const familyAgreements = (cs?.agreements || []).filter((a: any) => a.family === family);
                    const agreementsTotal = familyAgreements.reduce((acc: number, curr: any) => acc + (curr.amount || 0), 0);

                    csFamilyMap[key] = {
                        key: key,
                        controlSheetName: csName,
                        family: family,
                        controlSheetId: csId,
                        rowName: family === "Sin familia" ? csName : `${csName} - ${family}`,
                        agreementsTotal: agreementsTotal,
                        invoicesTotal: 0,
                        nnccTotal: transaction.total || 0
                    };
                }
            });

            setAvailableFamilies(Array.from(familiesSet).sort());

            let datum: any[] = Object.values(csFamilyMap)
                .filter((d: any) => selectedFamily === "Todas las familias" || d.family === selectedFamily)
                .map((d: any) => {
                    const facturasMinusNncc = d.invoicesTotal - d.nnccTotal;
                    const balance = d.agreementsTotal - facturasMinusNncc;
                    return {
                        ...d,
                        facturasMinusNncc,
                        balance
                    };
                });

            // Calculate totals
            let total = {
                "Acuerdos": 0,
                "Facturas - NNCC": 0,
                "Balance Total": 0
            };

            datum.forEach((d: any) => {
                total["Acuerdos"] += d.agreementsTotal;
                total["Facturas - NNCC"] += d.facturasMinusNncc;
                total["Balance Total"] += d.balance;
            });

            // Sort to ensure ControlSheets are grouped visually if user sorts by name
            datum.sort((a, b) => a.rowName.localeCompare(b.rowName));

            setData(datum);
            setResume(total);
        }

    }, [transactions, controlSheets, showAllApproved, selectedFamily]);


    let cols = [
        { name: <div>Hoja de control - Familia</div>, wrap: true, selector: (row: any) => row.rowName, sortable: true, width: "300px" },
        { name: <div>Acuerdos</div>, wrap: true, selector: (row: any) => `$${formatCurrency(row.agreementsTotal)}`, sortable: true, sortFunction: (a: any, b: any) => a.agreementsTotal - b.agreementsTotal },
        { name: <div>Facturas - NNCC</div>, wrap: true, selector: (row: any) => `$${formatCurrency(row.facturasMinusNncc)}`, sortable: true, sortFunction: (a: any, b: any) => a.facturasMinusNncc - b.facturasMinusNncc },
        {
            name: <div>Balance</div>,
            wrap: true,
            selector: (row: any) => row.balance,
            cell: (row: any) => <span className={row.balance < 0 ? "text-red-500 font-bold" : ""}>{row.balance < 0 ? `-$${formatCurrency(Math.abs(row.balance))}` : `$${formatCurrency(row.balance)}`}</span>,
            sortable: true,
            sortFunction: (a: any, b: any) => a.balance - b.balance
        },
    ];

    return (
        <Layout>
            <div className="container">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-bold">Dashboard General: Hojas de Control</h1>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                            <input
                                type="checkbox"
                                checked={showAllApproved}
                                onChange={(e) => setShowAllApproved(e.target.checked)}
                                className="form-checkbox h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span>Mostrar todas las facturas aprobadas e ingresadas (por defecto: sólo pagadas)</span>
                        </label>
                        <select
                            value={selectedFamily}
                            onChange={(e) => setSelectedFamily(e.target.value)}
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
                        >
                            <option value="Todas las familias">Todas las familias</option>
                            {availableFamilies.map((family) => (
                                <option key={family} value={family}>{family}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="flex gap-2 my-4">
                {Object.keys(resume).map((key: string) => {
                    return <InfoCard key={key} title={key} content={[
                        `$${formatCurrency(resume[key])}`
                    ]}
                    />
                })}
            </div>

            <DataTable
                columns={cols}
                data={data}
                noDataComponent={<div className="my-10">No hay datos para mostrar</div>}
                pagination
                paginationPerPage={30}
                paginationComponentOptions={{ noRowsPerPage: true }}
            />
        </Layout>
    );
}

export default GeneralControlSheetDashboardPage;
